import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { serverUrl, showNotification } from './App';
import { unregister } from './registerServiceWorker';
import { ApolloLink, Operation, NextLink, fromPromise, Observable } from 'apollo-link';
import { BatchHttpLink } from 'apollo-link-batch-http';
import {
    InMemoryCache,
    IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import { App } from './App';
import './index.css';
import { globalVariableDefaults } from './globals';
import { getMainDefinition } from 'apollo-utilities';
import { WebSocketLink } from 'apollo-link-ws';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { ApolloProvider } from 'react-apollo';
import { ApolloProvider as ApolloHookProvider } from '@apollo/react-hooks';
import ApolloClient from 'apollo-client/ApolloClient';
import { LicenseManager } from '@ag-grid-enterprise/core';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import MomentUtils from '@date-io/moment';
import { MatterManagementContextProvider } from './matterManagement/MatterManagementContext';
// import { EnquirySummaryContextProvider } from './enquirySummary/EnquirySummaryContext';
import { TasksSummaryContextProvider } from './tasksSummary/TasksSummaryContext';
import { isUploadRequest, multiPartLink } from './infrastructure/MultipartLink';
import TimerContextProvider from './contexts/TimerContext';
import './i18n';
import { MatterSummaryContextProvider } from './matterSummary/MatterSummaryContextProvider';
import { RefreshTokenMutation, TokenResponse } from './components/Login';
import { UserFeature } from './types/UserFeature';
import { appInsights } from './infrastructure/ApplicationInsights';
import { AppInsightsErrorBoundary } from './infrastructure/AppInsightsErrorBoundary';
import { createTheme, CssBaseline, MuiThemeProvider } from '@material-ui/core';

// tslint:disable-next-line:max-line-length
LicenseManager.setLicenseKey(
    // tslint:disable-next-line: max-line-length
    'Using_this_{AG_Grid}_Enterprise_key_{AG-096846}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{RED_RAIN_CORPORATION_PTY_LTD}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{RedView}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{RedView}_need_to_be_licensed___{RedView}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{11_March_2027}____[v3]_[01]_MTgwNDcyMzIwMDAwMA==4b41d65cdfa44ebc210ebdfdc83e4a52'
);

// tslint:disable-next-line:no-console
const GRAPHQL_ENDPOINT = serverUrl + '/api/graphql';

const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: {
        __schema: {
            types: [
                {
                    kind: 'UNION',
                    name: 'PartyDetails',
                    possibleTypes: [
                        { name: 'Staff' },
                        { name: 'Individual' },
                        { name: 'Organisation' },
                        { name: 'Referrer' }
                    ]
            },
                {
                    kind: 'UNION',
                    name: 'TaskDetails',
                    possibleTypes: [                       
                        { name: 'GeneralTask' },
                        { name: 'ClientTask' },
                        { name: 'MatterTask' },
                        { name: 'PhoneTask' },
                        { name: 'FollowUpTask' },
                    ]
                },
                {
                  kind: 'INTERFACE',
                  name: 'TaskDetailTypeInterface',
                  possibleTypes: [
                        { name: 'GeneralTask' },
                        { name: 'ClientTask' },
                        { name: 'MatterTask' },
                        { name: 'PhoneTask' },
                        { name: 'FollowUpTask' }
                    ]
                }
            ]
        }
    }
});

const httpLink = new BatchHttpLink({
    uri: GRAPHQL_ENDPOINT,
    credentials: 'include',
    // Requests are grouped into the same HTTP call when they land within the same
    // ~10ms window (batchInterval). Callers that don't want to be tied to whatever
    // else happens to be in flight at the same moment (e.g. a secondary fetch that
    // shouldn't hold up a page/drawer's main render) can opt into their own bucket
    // via context: { batchGroup: 'some-key' } on the client.query() call — everyone
    // else keeps today's behaviour (all sharing the default '' bucket).
    batchKey: (operation: Operation) => operation.getContext().batchGroup || ''
});

let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

const resolvePendingRequests = () => {
    pendingRequests.forEach(callback => callback());
    pendingRequests = [];
};

const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
            const authError = graphQLErrors.find(
                ({ extensions, message }) =>
                    (extensions && extensions.code === 'auth-required') ||
                    message === 'Not Authenticated'
            );

            if (authError) {
                // External pages manage their own session expiry — don't redirect to the internal logout route
                if (location.pathname.startsWith('/externalAccess/')) {
                    return;
                }

                // Skip retry for subscriptions — the WebSocket client will reconnect with the new token automatically
                const definition = getMainDefinition(operation.query);
                const isSubscription = definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
                if (isSubscription) {
                    return;
                }

                // Capture tenantId early — Logout.clearLogin() may remove it from sessionStorage during async operations
                const tenantId = sessionStorage.getItem('tenantId') || '';

                const context = operation.getContext();
                if (context.hasRetried) {
                    // Already retried once — redirect to login
                    appInsights.trackEvent({ name: 'TokenRefresh', properties: { status: 'retry-exhausted', operation: operation.operationName, tenantId: UserFeature.Tenant, userId: UserFeature.UserId } });
                    window.location.href = '/' + tenantId + '/logout';
                    return;
                }

                let forwardObservable: Observable<{}>;

                if (!isRefreshing) {
                    isRefreshing = true;

                    const refreshToken = sessionStorage.getItem('refreshTokenForAutoRefresh');

                    if (!refreshToken || !tenantId) {
                        appInsights.trackEvent({ name: 'TokenRefresh', properties: { status: 'no-refresh-token', operation: operation.operationName, tenantId: UserFeature.Tenant, userId: UserFeature.UserId } });
                        window.location.href = '/' + tenantId + '/logout';
                        return;
                    }

                    appInsights.trackEvent({ name: 'TokenRefresh', properties: { status: 'attempting', operation: operation.operationName, tenantId: UserFeature.Tenant, userId: UserFeature.UserId } });

                    forwardObservable = fromPromise(
                        client.mutate<{ refreshToken: TokenResponse }>({
                            mutation: RefreshTokenMutation,
                            variables: {
                                tenantId: tenantId,
                                refreshToken: refreshToken
                            }
                        }).then(results => {
                            const response = results.data?.refreshToken;

                            if (response && response.statusCode === 200) {
                                sessionStorage.setItem('accessToken', response.accessToken);
                                sessionStorage.setItem('refreshTokenForAutoRefresh', response.refreshToken);
                                UserFeature.Init();
                                appInsights.trackEvent({ name: 'TokenRefresh', properties: { status: 'success', operation: operation.operationName, tenantId: UserFeature.Tenant, userId: UserFeature.UserId } });
                                resolvePendingRequests();
                                return response.accessToken;
                            } else {
                                appInsights.trackEvent({ name: 'TokenRefresh', properties: {
                                    status: 'failed',
                                    operation: operation.operationName,
                                    tenantId: UserFeature.Tenant,
                                    userId: UserFeature.UserId,
                                    statusCode: response?.statusCode?.toString(),
                                    error: response?.error || response?.errorDescription
                                }});
                                resolvePendingRequests();
                                window.location.href = '/' + tenantId + '/logout';
                                return undefined;
                            }
                        }).catch((error) => {
                            appInsights.trackException({ exception: error instanceof Error ? error : new Error(String(error)), properties: { source: 'TokenRefresh', operation: operation.operationName, tenantId: UserFeature.Tenant, userId: UserFeature.UserId } });
                            resolvePendingRequests();
                            window.location.href = '/' + tenantId + '/logout';
                            return undefined;
                        }).finally(() => {
                            isRefreshing = false;
                        })
                    ).flatMap((newToken) => {
                        if (!newToken) {
                            return Observable.of();
                        }
                        operation.setContext({
                            headers: {
                                ...operation.getContext().headers,
                                authorization: `Bearer ${newToken}`
                            },
                            hasRetried: true
                        });
                        return forward(operation);
                    });
                } else {
                    // Another request is already refreshing — queue this one
                    forwardObservable = fromPromise(
                        new Promise<void>(resolve => {
                            pendingRequests.push(() => resolve());
                        })
                    ).flatMap(() => {
                        const newToken = sessionStorage.getItem('accessToken');
                        if (!newToken) {
                            return Observable.of();
                        }
                        operation.setContext({
                            headers: {
                                ...operation.getContext().headers,
                                authorization: `Bearer ${newToken}`
                            },
                            hasRetried: true
                        });
                        return forward(operation);
                    });
                }

                return forwardObservable;
            }

            // Non-auth errors — show notifications as before
            // eslint-disable-next-line array-callback-return
            graphQLErrors.map(({ message }) => {
                showNotification(null, message, 'error');
            });
        }
        if (networkError) {
            showNotification(
                'Network error',
                networkError.message,
                'error',
                'SingleNotification'
            );
        }
        return;
    }
);

const consoleLink = new ApolloLink(
    (operation: Operation, forward: NextLink) => {
        // console.log(`@starting request for ${operation.operationName}`, operation);
        return forward(operation).map(data => {
            // console.log(`@ending request for ${operation.operationName}`);
            return data;
        });
    }
);

export const wsClient = new SubscriptionClient(
    GRAPHQL_ENDPOINT.replace('https', 'wss'),
    {
        connectionParams: () => {
            // get the authentication token from session storage if it exists
            const token = sessionStorage.getItem('accessToken');
            return {
                Authorization: token ? `Bearer ${token}` : '',
                RequestSource: 'connectweb'
            };
        },
        lazy: true,
        reconnect: true
        // tslint:disable-next-line:align
    },
    null,
    ['graphql-ws', 'connectweb']
);

const webSocketLink = new WebSocketLink(wsClient);

const authLink = setContext((_, { headers }) => {
    // console.log(`@setting context for headers`);
    // get the authentication token from session storage if it exists
    const token = sessionStorage.getItem('accessToken');
    // return the headers to the context so httpLink can read them
    if (token) {
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : ''
            }
        };
    }

    return {
        headers: {
            ...headers
        }
    };
});

const requestLink = ApolloLink.split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    webSocketLink,
    consoleLink.concat(httpLink)
);

const multiPartRequestLink = ApolloLink.split(
    ({ variables }) => {
        return isUploadRequest(variables);
    },
    multiPartLink,
    requestLink    
);

const link = ApolloLink.from([authLink, errorLink, multiPartRequestLink]);

export const resetApolloClient = async () => {
    try {
        await client.resetStore();
    } catch (error) {
        // console.error('Error resetting client', error);
    }
};

export const cache = new InMemoryCache({ fragmentMatcher });

// const stateLink = withClientState({
//     cache,
//     defaults: globalVariableDefaults,
//     resolvers: {}
//   });

cache.writeData({
    data: {
        defaults: globalVariableDefaults
    }
});

export const client = new ApolloClient({
    connectToDevTools: true,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'ignore'
        },
        query: {
            fetchPolicy: 'cache-first',
            errorPolicy: 'all'
        },
        mutate: {
            errorPolicy: 'all'
        }
    },
    link: ApolloLink.from([
        // stateLink,
        errorLink,
        link
    ]),
    // link: errorLink.concat(link),
    cache: cache
    // This is what enables cancelation
    // queryDeduplication: false
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#5173FF',
    },
  },
});

ReactDOM.render(
    <AppInsightsErrorBoundary componentName="App">
        <ApolloProvider client={client}>
            <ApolloHookProvider client={client}>
                <TimerContextProvider>
                    {/* <EnquirySummaryContextProvider> */}
                    <MatterManagementContextProvider>
                        <TasksSummaryContextProvider>
                            <MatterSummaryContextProvider>
                                <MuiThemeProvider theme={theme}>
                                    <CssBaseline />
                                    <MuiPickersUtilsProvider utils={MomentUtils}>
                                        <App />
                                    </MuiPickersUtilsProvider>
                                </MuiThemeProvider>
                            </MatterSummaryContextProvider>
                        </TasksSummaryContextProvider>
                    </MatterManagementContextProvider>
                    {/* </EnquirySummaryContextProvider> */}
                </TimerContextProvider>
            </ApolloHookProvider>
        </ApolloProvider>
    </AppInsightsErrorBoundary>,
    document.getElementById('root')
);
unregister();